import React from 'react';
import { Alert } from '@mui/material';

/**
 * Yellow banner shown on the worker detail page when VCA or ID document
 * expiry falls within 3 months (or has already expired). Pure frontend
 * computation against the worker's fields — no backend cron.
 *
 * Renders nothing when neither doc is expiring.
 */
interface Props {
    vcaValidUntil?: string | Date | null;
    idDocumentValidUntil?: string | Date | null;
}

/** 3-month cutoff. Anything expiring closer than this banners up. */
const WARN_DAYS = 90;

function daysUntil(target: Date, now: Date): number {
    return Math.floor((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

function parseAsDate(value: string | Date | null | undefined): Date | null {
    if (!value) return null;
    const d = value instanceof Date ? value : new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
}

function describeStatus(label: string, target: Date | null, now: Date): string | null {
    if (!target) return null;
    const days = daysUntil(target, now);
    if (target.getTime() <= now.getTime()) {
        return `${label} is verlopen op ${target.toLocaleDateString('nl-NL')}`;
    }
    if (days < WARN_DAYS) {
        return `${label} verloopt over ${days} ${days === 1 ? 'dag' : 'dagen'}`;
    }
    return null;
}

export const DocumentExpiryBanner: React.FC<Props> = ({
    vcaValidUntil,
    idDocumentValidUntil,
}) => {
    const now = new Date();
    const vcaDate = parseAsDate(vcaValidUntil);
    const idDate = parseAsDate(idDocumentValidUntil);

    const messages: string[] = [];
    const vcaMsg = describeStatus('VCA', vcaDate, now);
    if (vcaMsg) messages.push(vcaMsg);
    const idMsg = describeStatus('ID-document', idDate, now);
    if (idMsg) messages.push(idMsg);

    if (messages.length === 0) return null;

    // If anything is past expiry, render error; otherwise warning.
    const anyExpired =
        (vcaDate && vcaDate.getTime() <= now.getTime())
        || (idDate && idDate.getTime() <= now.getTime());

    return (
        <Alert severity={anyExpired ? 'error' : 'warning'} sx={{ mb: 2 }}>
            {messages.join(' • ')}
        </Alert>
    );
};
